import styled, { css } from 'styled-components';

import { ANIM, LAYOUT_CHROME, LUMIO_DRAWER, LUMIO_DRAWER_ANIM } from '../../../../theme/tokens';

/* The LumioChat panel's own corner radius (LumioChat.styles `Root`, 26px) — the
 * floating sheet mirrors it so the drop shadow hugs the same rounded silhouette. */
const SHEET_RADIUS = '26px';

const NO_MOTION = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* Full-viewport overlay that captures backdrop clicks (→ close). A sibling of the
 * layout `Root` (rendered after MobileNav), so it sits above the chrome. Pointer
 * events are live only while open, so the closing/closed drawer never eats clicks
 * meant for the page underneath. */
export const Root = styled.div<{ $entered: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 45;
  display: flex;
  justify-content: flex-end;
  padding: ${LUMIO_DRAWER.panelInset};
  pointer-events: ${(p) => (p.$entered ? 'auto' : 'none')};
`;

/* The dimming layer — a flat 50% black wash (no blur, so the page stays legible
 * for context while Lumio is open). Fades its opacity (kept off the panel so the
 * sheet can slide independently of the dim). */
export const Scrim = styled.div<{ $entered: boolean }>`
  position: absolute;
  inset: 0;
  background: ${LUMIO_DRAWER.scrim};
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition: opacity ${LUMIO_DRAWER_ANIM.SCRIM_MS}ms ${LUMIO_DRAWER_ANIM.EASE};
  ${NO_MOTION}
`;

/* The floating sheet — holds the LumioChat panel at the same width as the desktop
 * column. Slides in from the right (translateX) and lifts in opacity; the matching
 * radius + shadow give it elevation off the dimmed page. */
export const Panel = styled.aside<{ $entered: boolean }>`
  position: relative;
  flex: 0 0 ${LUMIO_DRAWER.panelWidth};
  width: ${LUMIO_DRAWER.panelWidth};
  max-width: 100%;
  height: 100%;
  border-radius: ${SHEET_RADIUS};
  box-shadow: ${LUMIO_DRAWER.panelShadow};
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transform: translateX(${(p) => (p.$entered ? '0' : `calc(100% + ${LUMIO_DRAWER.panelInset})`)});
  transition:
    transform ${LUMIO_DRAWER_ANIM.PANEL_MS}ms ${LUMIO_DRAWER_ANIM.EASE},
    opacity ${LUMIO_DRAWER_ANIM.PANEL_MS}ms ${LUMIO_DRAWER_ANIM.EASE};

  /* Reduced motion: no slide — the scrim/opacity fade alone present the sheet. */
  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${LUMIO_DRAWER_ANIM.SCRIM_MS}ms ${LUMIO_DRAWER_ANIM.EASE};
    transform: none;
  }
`;

/* Circular close affordance hugging the sheet's outer-left edge (sits on the
 * scrim, in the gutter). A child of Panel, so it rides the slide-in for free.
 *
 * `top` lands the 40px disc's CENTRE on the TopBar control row's centre line, so
 * the close button reads as one of the header buttons (Lumio trigger / wallet /
 * brand selector / breadcrumb). That row centre sits at, from the viewport top:
 *   layout pad (LAYOUT_CHROME.pad) + the top bar's own 16px pad + half the 48px
 *   brand selector (24px).
 * The disc lives inside the Panel, which starts at the drawer's panelInset; so:
 *   top = pad + 16 + 24  −  panelInset  −  20 (half the 40px disc). */
export const CloseButton = styled.button`
  position: absolute;
  top: calc(${LAYOUT_CHROME.pad} + 16px + 24px - ${LUMIO_DRAWER.panelInset} - 20px);
  left: -52px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: ${LUMIO_DRAWER.closeBg};
  color: ${LUMIO_DRAWER.closeGlyph};
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${LUMIO_DRAWER_ANIM.EASE};

  &:hover,
  &:focus-visible {
    background: ${LUMIO_DRAWER.closeBgHover};
  }

  svg {
    display: block;
    width: 18px;
    height: 18px;
  }
`;
